// As 6 áreas da empresa (decisão do Lucas, 03/10/2026) — para o catálogo de
// serviços, a proposta e o contrato. A lista em si mora em
// `pages/financeiro-contas-receber/utils.ts` (`SETORES`), que já dava cor e
// ordem ao rateio das parcelas; aqui ela só ganha o formato de opção de Select
// e a regra da sub-área. Não crie outra lista de áreas: use esta.
import { SETORES } from '../pages/financeiro-contas-receber/utils';
import { CNAE_PADRAO_DA_AREA, cnaeValido } from './cnaes';

export { SETORES };

export type Subarea = { id: string; setor_chave: string; nome: string; ativo: boolean; ordem?: number; n_servicos?: number };
// O que se escolhe para um serviço: área, sub-área e código de serviço da NF.
export type EscolhaArea = { setor_chave: string | null; subarea_id: string | null; cnae: string | null };

// Trocar a área limpa a sub-área (ela é da área antiga) e acompanha o CNAE
// padrão — a não ser que a pessoa já tenha escolhido um código diferente do
// padrão da área em que estava.
export function aoTrocarArea(atual: EscolhaArea, novaArea: string | null): EscolhaArea {
  const cnaeEraPadrao = !atual.cnae || atual.cnae === CNAE_PADRAO_DA_AREA[atual.setor_chave || ''];
  return {
    setor_chave: novaArea,
    subarea_id: null,
    cnae: novaArea && cnaeEraPadrao ? (CNAE_PADRAO_DA_AREA[novaArea] || atual.cnae) : atual.cnae,
  };
}

export const AREAS_EMPRESA = Object.entries(SETORES)
  .sort((a, b) => a[1].ordem - b[1].ordem)
  .map(([chave, s]) => ({ chave, rotulo: s.rotulo, cor: s.cor }));

export const OPCOES_AREA = AREAS_EMPRESA.map(a => ({ value: a.chave, label: a.rotulo }));

export const rotuloDaArea = (chave?: string | null) => (chave && SETORES[chave]?.rotulo) || '';

// Busca sem acento e sem caixa — para o filterOption dos Selects.
export const semAcento = (t: unknown) =>
  String(t ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
export const casaBusca = (digitado: string, rotulo: unknown) => semAcento(rotulo).includes(semAcento(digitado));

// Sub-áreas ATIVAS de uma área (mais a que o serviço já usa, mesmo desativada).
export function subareasDaArea(subareas: Subarea[], chave?: string | null, manter?: string | null): Subarea[] {
  if (!chave) return [];
  return subareas
    .filter(s => s.setor_chave === chave && (s.ativo || s.id === manter))
    .sort((a, b) => (a.ordem ?? 100) - (b.ordem ?? 100) || a.nome.localeCompare(b.nome));
}

// A sub-área é obrigatória quando a área tem alguma ativa (mesma régua do servidor).
export const exigeSubarea = (subareas: Subarea[], chave?: string | null) =>
  !!chave && subareas.some(s => s.setor_chave === chave && s.ativo);

// O que falta para o serviço poder ser salvo — texto pronto para a tela, ou null.
// É a MESMA régua do servidor (`resolverArea`), conferida lado a lado no teste:
// a tela não pode acender o "Salvar" para um pedido que o servidor recusa.
// `subareaAtual` = a sub-área que o serviço já tem gravada (uma sub-área
// desativada só continua valendo em quem já estava nela).
export function faltaNoServico(
  f: { nome?: string; setor_chave?: string | null; subarea_id?: string | null; cnae?: string | null },
  subareas: Subarea[],
  subareaAtual?: string | null,
): string | null {
  if (!String(f.nome || '').trim()) return 'Informe o nome do serviço.';
  if (!f.setor_chave || !SETORES[f.setor_chave]) return 'Escolha a área do serviço.';
  if (f.subarea_id) {
    const s = subareas.find(x => x.id === f.subarea_id);
    if (!s || s.setor_chave !== f.setor_chave) return `Escolha de novo a sub-área: a escolhida não é de ${rotuloDaArea(f.setor_chave)}.`;
    if (!s.ativo && s.id !== subareaAtual) return 'Esta sub-área está desativada. Escolha outra.';
  } else if (exigeSubarea(subareas, f.setor_chave)) {
    return `Escolha a sub-área: ${rotuloDaArea(f.setor_chave)} tem sub-áreas.`;
  }
  if (!f.cnae) return 'Escolha o código de serviço da NF (CNAE).';
  if (!cnaeValido(f.cnae)) return `O código "${f.cnae}" não está na lista. Escolha o código de serviço da NF (CNAE).`;
  return null;
}
